import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Tag } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageHeader } from '../../components/AppShell';
import {
  Card,
  CardHeader,
  EmptyState,
  ListRow,
  Select,
  Spinner,
  Stat,
  StatusPill,
  complianceTone,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { formatDateTime, formatTime, humanise } from '../../lib/format';

interface SupervisorData {
  windowDays: number;
  compliance: {
    expected: number;
    done: number;
    missed: number;
    overdue: number;
    inProgress: number;
    rate: number | null;
  };
  missedChecks: Array<{
    scheduleId: string;
    templateName: string;
    machineCode: string;
    machineName: string;
    lineName: string;
    dueAt: string;
    status: string;
  }>;
  verificationQueue: Array<{
    id: string;
    submittedAt: string | null;
    totalItems: number;
    completedItems: number;
    abnormalCount: number;
    problemCount: number;
    shift: string | null;
    templateName: string;
    machineCode: string;
    machineName: string;
    operatorName: string;
    lineName: string;
  }>;
  problems: { open: number; critical: number; overdue: number; pendingVerification: number };
  recentAbnormalities: Array<{
    runId: string;
    machineCode: string;
    templateName: string;
    operatorName: string;
    abnormalCount: number;
    submittedAt: string | null;
    status: string;
  }>;
}

/** Answers the supervisor's question: what is abnormal? (directive §79) */
export function SupervisorDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const days = params.get('days') ?? '7';

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'supervisor', days],
    queryFn: () => api.get<SupervisorData>(`/api/dashboard/supervisor${qs({ days })}`),
  });

  if (isLoading || !data) return <Spinner label="Loading dashboard" />;

  return (
    <div>
      <PageHeader
        title="Supervisor"
        subtitle={`CILT compliance and abnormalities over the last ${data.windowDays} days`}
        actions={
          <Select
            value={days}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              next.set('days', event.target.value);
              setParams(next, { replace: true });
            }}
            aria-label="Time window"
            className="w-40"
          >
            <option value="1">Today</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </Select>
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat
            label="CILT compliance"
            value={data.compliance.rate === null ? '—' : `${data.compliance.rate}%`}
            hint={`${data.compliance.done} of ${data.compliance.expected} scheduled checks`}
            tone={
              data.compliance.rate === null
                ? 'neutral'
                : data.compliance.rate >= 95
                  ? 'ok'
                  : data.compliance.rate >= 80
                    ? 'warn'
                    : 'alarm'
            }
          />
          <Stat
            label="Missed checks"
            value={data.compliance.missed}
            hint={`${data.compliance.overdue} overdue now`}
            tone={data.compliance.missed > 0 ? 'alarm' : 'ok'}
            icon={AlertTriangle}
          />
          <Stat
            label="Awaiting my verification"
            value={data.verificationQueue.length}
            tone={data.verificationQueue.length > 0 ? 'warn' : 'ok'}
            icon={ClipboardCheck}
          />
          <Stat
            label="Open problems"
            value={data.problems.open}
            hint={
              data.problems.critical > 0
                ? `${data.problems.critical} critical · ${data.problems.overdue} overdue`
                : `${data.problems.overdue} overdue`
            }
            tone={data.problems.critical > 0 ? 'alarm' : data.problems.open > 0 ? 'warn' : 'ok'}
            icon={Tag}
          />
        </div>

        <Card>
          <CardHeader
            title="Verification queue"
            subtitle="Submitted checklists waiting for review"
          />
          {data.verificationQueue.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="Nothing waiting"
              message="Every submitted checklist on your lines has been reviewed."
            />
          ) : (
            <ul className="divide-y divide-steel-100">
              {data.verificationQueue.map((run) => (
                <li key={run.id}>
                  <ListRow onClick={() => navigate(`/checklists/${run.id}/review`)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-steel-900">
                          {run.machineCode} · {run.templateName}
                        </p>
                        <p className="text-xs text-steel-500">
                          {run.operatorName}
                          {run.shift ? ` · Shift ${run.shift}` : ''} · {run.lineName} ·{' '}
                          {formatDateTime(run.submittedAt)}
                        </p>
                        <p className="mt-1 text-xs font-semibold text-steel-700">
                          {run.completedItems} / {run.totalItems} completed
                          {run.abnormalCount > 0 ? (
                            <span className="text-warn-700"> · {run.abnormalCount} abnormal</span>
                          ) : null}
                          {run.problemCount > 0 ? (
                            <span className="text-alarm-700">
                              {' '}
                              · {run.problemCount} problem tag{run.problemCount === 1 ? '' : 's'}
                            </span>
                          ) : null}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs font-bold text-info-700">Review →</span>
                    </div>
                  </ListRow>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Missed and overdue checks" />
            {data.missedChecks.length === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="Nothing missed"
                message="Every scheduled CILT check in this window was performed on time."
              />
            ) : (
              <ul className="divide-y divide-steel-100">
                {data.missedChecks.map((check) => (
                  <li
                    key={`${check.scheduleId}-${check.dueAt}`}
                    className="flex items-start justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-steel-900">
                        {check.machineCode} · {check.templateName}
                      </p>
                      <p className="text-xs text-steel-500">
                        {check.lineName} · was due {formatDateTime(check.dueAt)}
                      </p>
                    </div>
                    <StatusPill
                      tone={complianceTone(check.status)}
                      label={humanise(check.status)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Recent abnormalities"
              action={
                <button
                  type="button"
                  onClick={() => navigate('/problems?openOnly=1')}
                  className="text-xs font-bold text-info-700 hover:underline"
                >
                  All problems
                </button>
              }
            />
            {data.recentAbnormalities.length === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="No abnormalities"
                message="No CILT check in this window found anything outside standard."
              />
            ) : (
              <ul className="divide-y divide-steel-100">
                {data.recentAbnormalities.map((entry) => (
                  <li key={entry.runId}>
                    <ListRow onClick={() => navigate(`/checklists/${entry.runId}/review`)}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-steel-900">
                            {entry.machineCode} · {entry.templateName}
                          </p>
                          <p className="text-xs text-steel-500">
                            {entry.operatorName} · {formatTime(entry.submittedAt)}
                          </p>
                        </div>
                        <StatusPill tone="warn" label={`${entry.abnormalCount} abnormal`} />
                      </div>
                    </ListRow>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
